'use client'

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  endOfDay, endOfMonth, endOfWeek, format,
  startOfDay, startOfMonth, startOfWeek,
} from 'date-fns'
import { es } from 'date-fns/locale'
import { DateRange } from 'react-day-picker'
import {
  Calendar as CalendarIcon, ChevronDown, ChevronRight, Coins, DollarSign, FileSpreadsheet, FileText,
  Loader2, Package, Percent, Receipt, RefreshCw, Search, TrendingUp, X, Zap,
} from 'lucide-react'

import { useSalesReport } from '@/hooks/useSalesReport'
import { useCompany } from '@/hooks/useCompany'
import { FunctionsService } from '@/services/functions.service'
import { cn, formatCurrency } from '@/lib/utils'
import { exportVariantsExcel, exportVariantsPdf, type VariantExportRow } from '@/lib/salesVariantsExport'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Header } from '../layout/Header'

type Preset = 'today' | 'week' | 'month' | 'custom'

const DEFAULT_RANGE: DateRange = {
  from: startOfDay(new Date()),
  to: endOfDay(new Date()),
}

// Normaliza para comparar sin distinguir mayúsculas ni acentos
const normalize = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()

const parseMoney = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const normalized = value.replace(/\s/g, '').replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

// Etiqueta de la variante/talla de una línea de venta.
// Prioriza el título de variante de Shopify; si no hay, usa el SKU; si nada, "Talla única".
const variantLabelOf = (item: any): string => {
  const vt = item?.variantTitle ?? item?.variant_title ?? item?.variant ?? item?.variantName
  if (typeof vt === 'string') {
    const t = vt.trim()
    if (t && t.toLowerCase() !== 'default title') return t
  }
  const sku = item?.sku
  if (typeof sku === 'string' && sku.trim()) return sku.trim()
  return 'Talla única'
}

const SIZE_OPTION_RE = /talla|size|tama/i

// Talla de cada variante del catálogo (variantId → valor de la opción "Talla"/"Size")
const sizesByVariantOf = (product: any): [string, string][] => {
  const options: any[] = Array.isArray(product?.options) ? product.options : []
  const idx = options.findIndex((o) => SIZE_OPTION_RE.test(String(o?.name ?? '')))
  if (idx < 0) return []
  const variants: any[] = Array.isArray(product?.variants) ? product.variants : []
  return variants
    .map((v): [string, string] => [String(v?.id ?? ''), String(v?.[`option${idx + 1}`] ?? '').trim()])
    .filter(([id, size]) => id && size)
}

// Talla de una línea de venta: catálogo por variantId; si no, primer segmento del título de variante
const sizeOf = (item: any, sizeByVariant: Map<string, string>): string => {
  const variantId = item?.variantId ?? item?.variant_id
  if (variantId != null) {
    const s = sizeByVariant.get(String(variantId))
    if (s) return s
  }
  const label = variantLabelOf(item)
  if (label === 'Talla única') return label
  if (label === item?.sku?.trim()) return 'Sin talla'
  return label.split('/')[0].trim() || 'Sin talla'
}

interface VariantBreakdown {
  size: string
  label: string
  units: number
  sales: number
  cost: number | null // costo total de esa talla (costo unitario × unidades) o null si no hay costo
}

interface MatchedProduct {
  name: string
  brand: string
  collections: string[]
  units: number
  sales: number
  orders: number
  unitCost: number | null // costo unitario del catálogo (null si no hay dato)
  cost: number | null     // costo total (unitCost * units) o null
  profit: number | null   // ganancia (sales - cost) o null
  margin: number | null   // margen % (profit / sales) o null
  variants: VariantBreakdown[] // desglose por talla/variante (unidades por cada una)
}

interface CatalogProduct {
  id: string
  title: string
  cost?: number
  vendor: string
  collections: string[]
  sizes: [string, string][] // [variantId, talla]
}

export function SalesSearchDashboard() {
  const company = useCompany()
  const { report, loading, error, getSalesReport } = useSalesReport()

  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE)
  const [preset, setPreset] = useState<Preset>('today')
  const [calOpen, setCalOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [catalog, setCatalog] = useState<CatalogProduct[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null)

  const toggleExpanded = useCallback((name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }, [])

  const handleGenerate = useCallback(
    async (range: DateRange | undefined, force = false) => {
      if (!range?.from || !range?.to) return
      // force = true ignora el caché del servidor y trae datos frescos de Shopify
      await getSalesReport(startOfDay(range.from), endOfDay(range.to), force)
    },
    [getSalesReport]
  )

  useEffect(() => {
    handleGenerate(DEFAULT_RANGE)
    // Cargar catálogo (con costos) desde el caché de Firestore para calcular ganancia
    FunctionsService.getProductsFromFirestore().then((response) => {
      if (response.data?.products) {
        setCatalog(
          response.data.products.map((p: any) => ({
            id: String(p.id),
            title: p.title ?? '',
            cost: Number(p.cost) || 0,
            vendor: typeof p.vendor === 'string' ? p.vendor.trim() : '',
            collections: Array.isArray(p.collections)
              ? p.collections.filter((c: unknown): c is string => typeof c === 'string' && c.trim() !== '')
              : [],
            sizes: sizesByVariantOf(p),
          }))
        )
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Mapas de costo por ID y por nombre normalizado
  const { costById, costByName } = useMemo(() => {
    const byId = new Map<string, number>()
    const byName = new Map<string, number>()
    catalog.forEach((p) => {
      const cost = Number(p.cost) || 0
      if (cost > 0) {
        byId.set(p.id, cost)
        const key = normalize(p.title)
        if (key && !byName.has(key)) byName.set(key, cost)
      }
    })
    return { costById: byId, costByName: byName }
  }, [catalog])

  // Marca/colecciones por ID y por nombre, y talla por variantId
  const { infoById, infoByName, sizeByVariant } = useMemo(() => {
    const byId = new Map<string, CatalogProduct>()
    const byName = new Map<string, CatalogProduct>()
    const sizes = new Map<string, string>()
    catalog.forEach((p) => {
      byId.set(p.id, p)
      const key = normalize(p.title)
      if (key && !byName.has(key)) byName.set(key, p)
      p.sizes.forEach(([variantId, size]) => sizes.set(variantId, size))
    })
    return { infoById: byId, infoByName: byName, sizeByVariant: sizes }
  }, [catalog])

  const applyPreset = useCallback(
    (next: Exclude<Preset, 'custom'>) => {
      const now = new Date()
      let range: DateRange

      if (next === 'today') {
        range = { from: startOfDay(now), to: endOfDay(now) }
      } else if (next === 'week') {
        range = { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) }
      } else {
        range = { from: startOfMonth(now), to: endOfMonth(now) }
      }

      setPreset(next)
      setDateRange(range)
      handleGenerate(range)
    },
    [handleGenerate]
  )

  const handleCalendarSelect = useCallback((range: DateRange | undefined) => {
    if (!range?.from) return
    setPreset('custom')
    setDateRange({
      from: startOfDay(range.from),
      to: range.to ? endOfDay(range.to) : endOfDay(range.from),
    })
  }, [])

  const dateLabel = useMemo(() => {
    const { from, to } = dateRange
    if (!from || !to) return 'Selecciona rango'
    if (format(from, 'yyyy-MM-dd') === format(to, 'yyyy-MM-dd')) {
      return format(from, "d 'de' MMMM, yyyy", { locale: es })
    }
    return `${format(from, 'dd/MM/yy')} – ${format(to, 'dd/MM/yy')}`
  }, [dateRange])

  // Agrega los productos vendidos (de todas las órdenes del período)
  const allProducts = useMemo((): MatchedProduct[] => {
    const orders: any[] = (report as any)?.orders ?? []
    if (!orders.length) return []

    const map = new Map<
      string,
      {
        name: string
        units: number
        sales: number
        orderIds: Set<string>
        productIds: Set<string>
        variants: Map<string, { size: string; units: number; sales: number }>
      }
    >()

    orders.forEach((order) => {
      const orderId = String(order.id ?? order.order_number ?? Math.random())
      const items: any[] = order.products ?? order.line_items ?? []
      items.forEach((item) => {
        const name: string = item.productName ?? item.name ?? 'Producto sin nombre'
        const key = normalize(name)
        const qtyRaw = Number(item.units ?? item.quantity ?? 0)
        const qty = Number.isFinite(qtyRaw) ? qtyRaw : 0
        const totalSalesRaw = parseMoney(item.totalSales)
        const price = parseMoney(item.price)
        const sales = totalSalesRaw > 0 ? totalSalesRaw : price * qty

        const productIdRaw =
          item.productId ?? item.product_id ?? item.id ?? item.productID ?? null
        const productId = productIdRaw != null ? String(productIdRaw) : undefined

        const vLabel = variantLabelOf(item)
        const vSize = sizeOf(item, sizeByVariant)

        const current = map.get(key)
        if (current) {
          current.units += qty
          current.sales += sales
          current.orderIds.add(orderId)
          if (productId) current.productIds.add(productId)
          const v = current.variants.get(vLabel) ?? { size: vSize, units: 0, sales: 0 }
          v.units += qty
          v.sales += sales
          current.variants.set(vLabel, v)
        } else {
          map.set(key, {
            name,
            units: qty,
            sales,
            orderIds: new Set([orderId]),
            productIds: new Set(productId ? [productId] : []),
            variants: new Map([[vLabel, { size: vSize, units: qty, sales }]]),
          })
        }
      })
    })

    return Array.from(map.values()).map((p) => {
      // Costo unitario: primero por ID de producto, si no por nombre
      let unitCost: number | null = null
      for (const id of p.productIds) {
        const c = costById.get(id)
        if (c && c > 0) { unitCost = c; break }
      }
      if (unitCost == null) {
        const c = costByName.get(normalize(p.name))
        if (c && c > 0) unitCost = c
      }

      const cost = unitCost != null ? unitCost * p.units : null
      const profit = cost != null ? p.sales - cost : null
      const margin = profit != null && p.sales > 0 ? (profit / p.sales) * 100 : null

      let info: CatalogProduct | undefined
      for (const id of p.productIds) {
        info = infoById.get(id)
        if (info) break
      }
      info ??= infoByName.get(normalize(p.name))

      const variants: VariantBreakdown[] = Array.from(p.variants.entries())
        .map(([label, v]) => ({
          size: v.size,
          label,
          units: v.units,
          sales: v.sales,
          cost: unitCost != null ? unitCost * v.units : null,
        }))
        .sort((a, b) => b.units - a.units)

      return {
        name: p.name,
        brand: info?.vendor || 'Sin marca',
        collections: info?.collections ?? [],
        units: p.units,
        sales: p.sales,
        orders: p.orderIds.size,
        unitCost,
        cost,
        profit,
        margin,
        variants,
      }
    })
  }, [report, costById, costByName, infoById, infoByName, sizeByVariant])

  const term = normalize(search)

  const matched = useMemo(() => {
    const list = term
      ? allProducts.filter((p) => normalize(p.name).includes(term))
      : allProducts
    return [...list].sort((a, b) => b.sales - a.sales)
  }, [allProducts, term])

  const totals = useMemo(() => {
    return matched.reduce(
      (acc, p) => {
        acc.sales += p.sales
        acc.units += p.units
        acc.orders += p.orders
        acc.cost += p.cost ?? 0
        acc.profit += p.profit ?? 0
        if (p.cost == null) acc.missingCost += 1
        return acc
      },
      { sales: 0, units: 0, orders: 0, cost: 0, profit: 0, missingCost: 0 }
    )
  }, [matched])

  const totalMargin = totals.sales > 0 ? (totals.profit / totals.sales) * 100 : 0

  // Exporta lo que se ve en pantalla (respeta búsqueda y período) a nivel de variante
  const handleExport = useCallback(
    async (kind: 'excel' | 'pdf') => {
      const rows: VariantExportRow[] = matched.flatMap((p) =>
        p.variants.map((v) => ({
          product: p.name,
          brand: p.brand,
          collections: p.collections,
          size: v.size,
          variant: v.label,
          units: v.units,
          sales: v.sales,
          cost: v.cost,
        }))
      )
      const { from, to } = dateRange
      const meta = {
        companyName: company.name,
        period: dateLabel,
        search: search.trim(),
        fileBase: `ventas-variantes_${from ? format(from, 'yyyy-MM-dd') : ''}_${to ? format(to, 'yyyy-MM-dd') : ''}`,
      }
      setExporting(kind)
      try {
        if (kind === 'excel') await exportVariantsExcel(rows, meta)
        else await exportVariantsPdf(rows, meta)
      } catch (err) {
        console.error('Error exportando variantes:', err)
        alert('No se pudo generar el archivo. Intenta de nuevo.')
      } finally {
        setExporting(null)
      }
    },
    [matched, dateRange, dateLabel, search, company.name]
  )

  const summaryCards = [
    { title: 'Venta total', value: formatCurrency(totals.sales), icon: DollarSign, color: 'text-emerald-400' },
    { title: 'Costo total', value: formatCurrency(totals.cost), icon: Coins, color: 'text-amber-400' },
    { title: 'Ganancia', value: formatCurrency(totals.profit), icon: TrendingUp, color: totals.profit >= 0 ? 'text-emerald-400' : 'text-red-400' },
    { title: 'Margen', value: `${totalMargin.toFixed(1)}%`, icon: Percent, color: totalMargin >= 0 ? 'text-blue-400' : 'text-red-400' },
    { title: 'Unidades', value: totals.units.toLocaleString('es-ES'), icon: Package, color: 'text-blue-400' },
    { title: 'Órdenes', value: totals.orders.toLocaleString('es-ES'), icon: Receipt, color: 'text-purple-400' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.015]">
        <img src={company.watermarkUrl || '/nibpr.svg'} alt="" className="h-96 w-auto select-none" />
      </div>

      <div className="relative mx-auto w-full max-w-[1400px] px-4 py-8 pb-28 sm:px-6 lg:px-10">
        <Header />

        <div className="mb-6 hidden md:block">
          <h1 className="text-2xl font-bold text-white">Buscador de ventas</h1>
          <p className="text-sm text-white/50">
            Calcula cuánto vendiste de un producto en un período.
          </p>
        </div>

        {/* ── Controles ────────────────────────────────────────────────── */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-xl">
          <CardContent className="space-y-4 p-4 sm:p-6">
            {/* Buscador */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar producto por nombre… (ej. camiseta, gorra)"
                className="border-white/15 bg-white/10 pl-9 pr-9 text-white placeholder:text-white/40 focus-visible:ring-blue-500"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition hover:text-white"
                  aria-label="Limpiar búsqueda"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Presets + calendario */}
            <div className="flex flex-wrap items-center gap-2">
              {([
                { key: 'today', label: 'Hoy' },
                { key: 'week', label: 'Esta semana' },
                { key: 'month', label: 'Este mes' },
              ] as const).map(({ key, label }) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  className={cn(
                    'flex-shrink-0 border-white/10 bg-white/10 text-white hover:bg-white/20',
                    preset === key && 'border-blue-500 bg-blue-500 text-white hover:bg-blue-600'
                  )}
                  onClick={() => applyPreset(key)}
                >
                  {label}
                </Button>
              ))}

              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'flex-shrink-0 border-white/10 bg-white/10 text-white hover:bg-white/20',
                      preset === 'custom' && 'border-blue-500 bg-blue-500 text-white hover:bg-blue-600'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateLabel}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto border-white/20 bg-gray-900 p-0" align="start">
                  <Calendar
                    mode="range"
                    numberOfMonths={2}
                    locale={es}
                    selected={dateRange}
                    defaultMonth={dateRange.from}
                    onSelect={handleCalendarSelect}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Button
                size="sm"
                onClick={() => handleGenerate(dateRange)}
                disabled={loading}
                className="flex-shrink-0 gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Generar
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={() => handleGenerate(dateRange, true)}
                disabled={loading}
                title="Ignora el caché y trae datos frescos de Shopify (más lento)"
                className="flex-shrink-0 gap-2 border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                Forzar datos Shopify
              </Button>

              <div className="flex gap-2 sm:ml-auto">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleExport('excel')}
                  disabled={loading || exporting !== null || matched.length === 0}
                  title="Descargar variantes (tallas) agrupadas por talla, marca y colección"
                  className="flex-shrink-0 gap-2 border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                >
                  {exporting === 'excel' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                  Excel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleExport('pdf')}
                  disabled={loading || exporting !== null || matched.length === 0}
                  title="Descargar variantes (tallas) agrupadas por talla, marca y colección"
                  className="flex-shrink-0 gap-2 border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"
                >
                  {exporting === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  PDF
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {/* ── Resumen ──────────────────────────────────────────────────── */}
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          {summaryCards.map((card) => (
            <Card key={card.title} className="border-white/10 bg-white/5 text-white backdrop-blur">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 pb-1 pt-4">
                <CardDescription className="text-xs text-white/60">{card.title}</CardDescription>
                <card.icon className={cn('h-3.5 w-3.5 flex-shrink-0', card.color)} />
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {loading ? (
                  <Skeleton className="h-7 w-24 bg-white/10" />
                ) : (
                  <p className={cn('text-xl font-bold tabular-nums', card.color)}>{card.value}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Resultados ───────────────────────────────────────────────── */}
        <Card className="mt-6 border-white/10 bg-white/5 text-white backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-base">
              {term ? (
                <>Resultados para “{search.trim()}”</>
              ) : (
                'Todos los productos vendidos'
              )}
            </CardTitle>
            <CardDescription className="text-white/50">
              {dateLabel} · {matched.length} producto{matched.length === 1 ? '' : 's'}
              {totals.missingCost > 0 && (
                <span className="ml-1 text-amber-300/80">
                  · {totals.missingCost} sin costo en catálogo (ganancia parcial)
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full bg-white/10" />
            ) : matched.length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/15 p-10 text-center text-white/60">
                {term
                  ? 'Ningún producto coincide con la búsqueda en este período.'
                  : 'No hay ventas en el rango seleccionado.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/60">Producto</TableHead>
                      <TableHead className="text-right text-white/60">Unidades</TableHead>
                      <TableHead className="text-right text-white/60">Venta total</TableHead>
                      <TableHead className="text-right text-white/60">Costo</TableHead>
                      <TableHead className="text-right text-white/60">Ganancia</TableHead>
                      <TableHead className="text-right text-white/60">Margen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matched.map((p) => {
                      const isOpen = expanded.has(p.name)
                      const showToggle =
                        p.variants.length > 1 ||
                        (p.variants.length === 1 && p.variants[0].label !== 'Talla única')
                      return (
                        <Fragment key={p.name}>
                          <TableRow
                            className={cn('border-white/10', showToggle && 'cursor-pointer hover:bg-white/5')}
                            onClick={showToggle ? () => toggleExpanded(p.name) : undefined}
                          >
                            <TableCell className="font-medium text-white">
                              <div className="flex items-center gap-2">
                                {showToggle ? (
                                  isOpen ? (
                                    <ChevronDown className="h-4 w-4 flex-shrink-0 text-white/50" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-white/50" />
                                  )
                                ) : (
                                  <span className="inline-block w-4 flex-shrink-0" />
                                )}
                                <span>{p.name}</span>
                                <span className="text-xs text-white/40">
                                  {p.orders} orden{p.orders === 1 ? '' : 'es'}
                                </span>
                                {showToggle && (
                                  <span className="text-xs text-white/30">
                                    · {p.variants.length} talla{p.variants.length === 1 ? '' : 's'}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-white/90">
                              {p.units.toLocaleString('es-ES')}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-emerald-400">
                              {formatCurrency(p.sales)}
                            </TableCell>
                            <TableCell className="text-right text-amber-300">
                              {p.cost != null ? formatCurrency(p.cost) : <span className="text-white/30">—</span>}
                            </TableCell>
                            <TableCell
                              className={cn(
                                'text-right font-semibold',
                                p.profit == null ? 'text-white/30' : p.profit >= 0 ? 'text-emerald-400' : 'text-red-400'
                              )}
                            >
                              {p.profit != null ? formatCurrency(p.profit) : '—'}
                            </TableCell>
                            <TableCell
                              className={cn(
                                'text-right',
                                p.margin == null ? 'text-white/30' : p.margin >= 0 ? 'text-blue-300' : 'text-red-400'
                              )}
                            >
                              {p.margin != null ? `${p.margin.toFixed(1)}%` : '—'}
                            </TableCell>
                          </TableRow>

                          {isOpen && showToggle && (
                            <TableRow className="border-white/10 bg-white/[0.03] hover:bg-white/[0.03]">
                              <TableCell colSpan={6} className="py-3">
                                <div className="flex flex-wrap gap-1.5 pl-6">
                                  {p.variants.map((v) => (
                                    <span
                                      key={v.label}
                                      className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs"
                                    >
                                      <span className="text-white/70">{v.label}</span>
                                      <span className="font-semibold text-white">
                                        ×{v.units.toLocaleString('es-ES')}
                                      </span>
                                      <span className="text-white/20">·</span>
                                      <span className="text-emerald-400/80">{formatCurrency(v.sales)}</span>
                                      <span className="text-white/20">·</span>
                                      <span className="text-amber-300/80">
                                        {v.cost != null ? `costo ${formatCurrency(v.cost)}` : 'sin costo'}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      )
                    })}
                    {matched.length > 1 && (
                      <TableRow className="border-white/10 bg-white/5">
                        <TableCell className="font-bold text-white">Total</TableCell>
                        <TableCell className="text-right font-bold text-white">
                          {totals.units.toLocaleString('es-ES')}
                        </TableCell>
                        <TableCell className="text-right font-bold text-emerald-400">
                          {formatCurrency(totals.sales)}
                        </TableCell>
                        <TableCell className="text-right font-bold text-amber-300">
                          {formatCurrency(totals.cost)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right font-bold',
                            totals.profit >= 0 ? 'text-emerald-400' : 'text-red-400'
                          )}
                        >
                          {formatCurrency(totals.profit)}
                        </TableCell>
                        <TableCell
                          className={cn('text-right font-bold', totalMargin >= 0 ? 'text-blue-300' : 'text-red-400')}
                        >
                          {totalMargin.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default SalesSearchDashboard
